// src/App.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  useAuthStore,
  useUIStore,
  useOrgInstallStore,
  useWebSocketStore,
  useDevicesStore,
  useCredentialsStore,
  useBulkOperationsStore,
} from "./stores";
import "./App.css";
import config from "./config";
import logger from "./utils/logger";
import AppFooter from "./components/AppFooter";
import ModalsContainer from "./components/ModalsContainer";
import ViewContainer from "./components/ViewContainer";
import LoadingModal from "./LoadingModal";
import { useDeviceOperations } from "./hooks/useDeviceOperations";
import { useDeviceActions } from "./hooks/useDeviceActions";
import { useOrganizationSettings } from "./hooks/useOrganizationSettings";
import { useThemePreferences } from "./hooks/useThemePreferences";
import { useViewNavigation } from "./hooks/useViewNavigation";
import { useModalManagement } from "./hooks/useModalManagement";
import { useDisplayNameEditor } from "./hooks/useDisplayNameEditor";
import { useCredentialsSettingsEditor } from "./hooks/useCredentialsSettingsEditor";
import { useOrganizationManagement } from "./hooks/useOrganizationManagement";
import { useViewProps } from "./hooks/useViewProps";
import { useModalProps } from "./hooks/useModalProps";
import { useEligibleDevices } from "./hooks/useEligibleDevices";
import { useCredentialEventTracking } from "./hooks/useCredentialEventTracking";
import { useInstallationHandlers } from "./hooks/useInstallationHandlers";
import { useWebSocketMessageRouter } from "./hooks/useWebSocketMessageRouter";
import { useContextSync } from "./hooks/useContextSync";
import { useWebSocketConnection } from "./hooks/useWebSocketConnection";
import { useDeviceDataLoader } from "./hooks/useDeviceDataLoader";
import { useCollapseStates } from "./hooks/useCollapseStates";
import { resetSectionCollapse } from "./hooks/useSectionCollapse";
import { useDeviceSelection } from "./hooks/useDeviceSelection";
import { useAlertManagement } from "./hooks/useAlertManagement";
import { useActionHistory } from "./hooks/useActionHistory";
import { useConfigError } from "./hooks/useConfigError";
import { useLoadingStates } from "./hooks/useLoadingStates";
import { useNotification } from "./hooks/useNotification";
import { usePatronData } from "./hooks/usePatronData";
import { useActionTimeouts } from "./hooks/useActionTimeouts";
import { useLocalStoragePersistence } from "./hooks/useLocalStoragePersistence";
import { useBulkLockdownSync } from "./hooks/useBulkLockdownSync";
import { useDataLoader } from "./hooks/useDataLoader";
import { useActionEvents } from "./hooks/useActionEvents";
import { useCredentialEvents } from "./hooks/useCredentialEvents";
import { useDeviceEvents } from "./hooks/useDeviceEvents";
import { useSecurityEvents } from "./hooks/useSecurityEvents";
import { useSystemEvents } from "./hooks/useSystemEvents";
import { useWebSocketHandlers } from "./hooks/useWebSocketHandlers";
import { useRefreshActions } from "./hooks/useRefreshActions";
import { useBulkActions } from "./hooks/useBulkActions";
import { useCredentialManagement } from "./hooks/useCredentialManagement";
import WebSocketDebugPanel from "./components/WebSocketDebugPanel";
import {
  authenticatedFetch,
  setAuthFunctions,
  clearAuthFunctions,
  getStatusStore,
  removeStatusFromStore,
  makePendingKey,
  FULL_SYNC_DEBOUNCE_MS,
} from "./utils/appContentUtils";

// --- Main App Component ---
function AppContent({
  orgSettings: propOrgSettings,
  onToggleDevStreams,
  devStreamsVisible,
  devStreamEvents = [],
  onSwitchOrganization,
  onSwitchInstallation,
  onRefreshOrganizations,
  onRefreshInstallations,
  onChangeOrganization,
  onChangeInstallation,
  onUpdateInstallationSubscription,
  onSetShowInstallationManagement,
  onSetShowOrganizationManagement,
}) {
  // --- Auth Store (Zustand) ---
  const logout = useAuthStore((state) => state.logout);
  const getAccessToken = useAuthStore((state) => state.getAccessToken);
  const user = useAuthStore((state) => state.user);

  // --- Org/Install Store (Zustand) - Individual selectors to avoid object recreation ---
  const organizationId = useOrgInstallStore(
    (state) => state.selectedOrganization?.orgId,
  );
  const organizationName = useOrgInstallStore(
    (state) => state.selectedOrganization?.name,
  );
  const installationId = useOrgInstallStore(
    (state) => state.selectedInstallation?.installationId,
  );
  const installationName = useOrgInstallStore(
    (state) => state.selectedInstallation?.name,
  );
  const organizations = useOrgInstallStore((state) => state.organizations);
  const selectedOrganization = useOrgInstallStore(
    (state) => state.selectedOrganization,
  );
  const navigateToOrgSelector = useOrgInstallStore(
    (state) => state.navigateToOrgSelector,
  );
  const navigateToInstallationSelector = useOrgInstallStore(
    (state) => state.navigateToInstallationSelector,
  );

  // Create context object with useMemo to prevent recreation
  const orgInstallContext = React.useMemo(
    () => ({
      organizationId,
      organizationName,
      installationId,
      installationName,
      organizations,
      installations: selectedOrganization?.installations || [],
      changeOrganization: onChangeOrganization || navigateToOrgSelector,
      changeInstallation:
        onChangeInstallation || navigateToInstallationSelector,
      refreshOrganizations: onRefreshOrganizations,
      refreshInstallations: onRefreshInstallations,
      updateInstallationSubscription: onUpdateInstallationSubscription,
      setShowInstallationManagement: onSetShowInstallationManagement,
      setShowOrganizationManagement: onSetShowOrganizationManagement,
    }),
    [
      organizationId,
      organizationName,
      installationId,
      installationName,
      organizations,
      selectedOrganization,
      navigateToOrgSelector,
      navigateToInstallationSelector,
      onChangeOrganization,
      onChangeInstallation,
      onRefreshOrganizations,
      onRefreshInstallations,
      onUpdateInstallationSubscription,
      onSetShowInstallationManagement,
      onSetShowOrganizationManagement,
    ],
  );

  // Set the logout and getAccessToken functions for the authenticated fetch wrapper
  useEffect(() => {
    setAuthFunctions(logout, getAccessToken);
    return () => {
      clearAuthFunctions();
    };
  }, [logout, getAccessToken]);

  // --- Device Operations Hooks ---
  // These hooks provide all device data fetching and action execution functions
  const {
    fetchDevices,
    fetchDeviceState,
    fetchDeviceStateReadOnly,
    invalidateDeviceStateCache,
    setInstallationSyncCache,
    invalidateInstallationSyncCache,
  } = useDeviceOperations(authenticatedFetch);

  const { updateDeviceDisplayName, updateCredentialsSetting } = useDeviceActions(
    authenticatedFetch,
    invalidateDeviceStateCache,
  );

  // Theme and Navigation Hooks
  const { theme, toggleTheme, activeLogo, toggleLogo } = useThemePreferences();
  const {
    currentView,
    navigateToDevices,
    navigateToPatrons,
    deviceViewMode,
    handleViewModeChange,
  } = useViewNavigation();

  // Modal Management Hook
  // UI Store (remaining states after hook extraction)
  const {
    isInstallationManagementOpen,
    managedOrganizationId,
    setInstallationManagementOpen,
    isDeviceClaimingModalOpen,
    setIsDeviceClaimingModalOpen,
    isInstallationActionsOpen,
    setIsInstallationActionsOpen,
    isOrganizationManagementOpen,
    closeOrganizationManagementModal,
    setIsOrganizationManagementOpen,
    // Additional modal states
    isBulkStatusModalOpen,
    setBulkStatusModalOpen,
    showPasswordModal,
    closePasswordModal,
    isAdvancedSettingsOpen,
    openAdvancedSettings,
    closeAdvancedSettings,
  } = useModalManagement();

  // Device Selection Hook
  const { selectedDeviceId, selectedDeviceIdRef, setSelectedDeviceId } =
    useDeviceSelection();

  // Collapse States Hook (needs selectedDeviceId for per-device persistence)
  const {
    isDeviceStateCollapsed,
    toggleDeviceStateCollapsed,
    isCredentialGridCollapsed,
    toggleCredentialGridCollapsed,
    isLastActionCollapsed,
    toggleLastActionCollapsed,
    isDeviceActionsCollapsed,
    toggleDeviceActionsCollapsed,
    isDeviceSettingsCollapsed,
    toggleDeviceSettingsCollapsed,
  } = useCollapseStates({ selectedDeviceId });

  // Alert Management Hook
  const {
    forcedEntryAlert,
    clearForcedEntryAlert,
    proppedDoorAlert,
    clearProppedDoorAlert,
    eventDeniedAlert,
    clearEventDeniedAlert,
  } = useAlertManagement();

  // Action History Hook
  const { actionHistory, setActionHistory, lastActionRef } = useActionHistory();

  // Config Error Hook
  const { configError, setConfigError } = useConfigError();

  // Loading States Hook
  const {
    isRefreshing,
    isSyncing,
    setSyncing,
    stopRefreshing,
    // isSavingDisplayName now managed by useDisplayNameEditor (Phase 1 Days 43-45)
    isDownloadingSettings,
    loadingAction,
  } = useLoadingStates();

  // Computed loading state - true if any loading operation that should hide the entire UI is active
  // NOTE: loadingAction is intentionally EXCLUDED - it should only disable buttons, not hide the UI
  const isLoading = isRefreshing || isSyncing;

  // Notification Hook
  const { showNotification, notification, clearNotification } = useNotification();

  // Component ordering for drag-and-drop in detailed view
  const DEFAULT_COMPONENTS = [
    { id: "deviceState" },
    { id: "deviceActions" },
    { id: "lastActionStatus" },
    { id: "deviceCredentials" },
    { id: "deviceSettings" },
  ];

  const [components, setComponents] = useState(() => {
    try {
      const saved = localStorage.getItem("rw_component_order");
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaultIds = new Set(DEFAULT_COMPONENTS.map((c) => c.id));
        const savedIds = new Set(parsed.map((c) => c.id));
        // Start with saved order, appending any newly added components at the end
        const merged = parsed.filter((c) => defaultIds.has(c.id));
        DEFAULT_COMPONENTS.forEach((c) => { if (!savedIds.has(c.id)) merged.push(c); });
        return merged;
      }
    } catch (e) {}
    return DEFAULT_COMPONENTS;
  });

  // UI Store (remaining states after hook extraction)
  const {
    // Display name editing now managed by useDisplayNameEditor hook (Phase 1 Days 43-45)
    // Bulk action states
    bulkActionResults,
    clearBulkActionResults,
    bulkActionStatuses,
    setBulkActionStatuses,
    clearBulkActionStatuses,
    bulkActionTimestamps,
    updateBulkActionTimestamp,
    clearBulkActionTimestamps,
    bulkActionType,
    // Collapse state setters (for restoring per-device saved states)
    setDeviceStateCollapsed,
    setCredentialGridCollapsed,
    setLastActionCollapsed,
    setDeviceActionsCollapsed,
    setDeviceSettingsCollapsed,
  } = useUIStore();

  // WebSocket Store (don't subscribe to registerMessageHandler to avoid loops)
  const {
    connectionStatus,
    isAutoReconnectEnabled,
    showReconnectModal: isModalEnabled,
    setAutoReconnectEnabled,
    setShowReconnectModal: setIsModalEnabled,
    connect: connectWebSocketStore,
    disconnect: disconnectWebSocketStore,
  } = useWebSocketStore();

  // Devices Store
  const {
    devices,
    deviceStates,
    connectivityStatusUpdateTimes,
    setDevices,
    updateMultipleDeviceStates,
    refreshDevices: refreshDevicesFromStore,
  } = useDevicesStore();

  // Credentials Store
  const {
    credentials,
    credentialsLoaded,
    patronsMap,
    pendingDeletionCredentials,
    pendingAdditionCredentials,
    pendingDeletionPatrons,
    pendingFullCredentialDeletions,
    devicesWithPendingDeletions,
    offlineDeviceCredentials,
    setCredentials,
    setCredentialsLoaded,
    setPatronsMap,
    setPendingDeletionCredentials,
    setPendingAdditionCredentials,
    setPendingDeletionPatrons,
    setPendingFullCredentialDeletions,
    setDevicesWithPendingDeletions,
    setOfflineDeviceCredentials,
  } = useCredentialsStore();

  // Bulk Operations Store
  const {
    updateDeviceTracking,
    checkResetBulkLockdownTracking,
    setLastLockdownEventTime,
  } = useBulkOperationsStore();

  // Data Loader Hook
  // Must come AFTER all stores so showNotification, setDevices, etc. are available
  const {
    loadDataForDevice,
    triggerInstallationSync,
    loadInitialDevices,
    credentialsLoadStartTime,
  } = useDataLoader({
    installationId,
    authenticatedFetch,
    showNotification,
    setDevices,
    setSelectedDeviceId,
    setActionHistory,
    devices,
    selectedDeviceIdRef,
    getStatusStore,
    setConfigError,
  });

  // --- Additional State Management ---
  // Action history limit (for UI display)
  const [actionHistoryLimit, setActionHistoryLimit] = useState(5); // Default limit
  const MAX_HISTORY_IN_MEMORY = 50; // Keep a larger buffer in memory than shown

  // Drag-and-drop handler for component ordering
  const handleResetLayout = () => {
    // Reset component order to default
    setComponents(DEFAULT_COMPONENTS);
    try { localStorage.removeItem("rw_component_order"); } catch (e) {}

    // Expand all panels in UIStore
    setDeviceStateCollapsed(false);
    setCredentialGridCollapsed(false);
    setLastActionCollapsed(false);
    setDeviceActionsCollapsed(false);
    setDeviceSettingsCollapsed(false);

    // Clear all per-device collapse state entries from localStorage
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("rw_collapse_"))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) {}

    // Reset section collapse states to defaults
    resetSectionCollapse();
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(components);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setComponents(items);
    try { localStorage.setItem("rw_component_order", JSON.stringify(items)); } catch (e) {}
  };

  // Restore per-device collapse states when the selected device changes
  useEffect(() => {
    if (!selectedDeviceId) return;
    try {
      const saved = localStorage.getItem(`rw_collapse_${selectedDeviceId}`);
      if (!saved) return;
      const s = JSON.parse(saved);
      if (s.deviceState !== undefined) setDeviceStateCollapsed(s.deviceState);
      if (s.credentialGrid !== undefined) setCredentialGridCollapsed(s.credentialGrid);
      if (s.lastAction !== undefined) setLastActionCollapsed(s.lastAction);
      if (s.deviceActions !== undefined) setDeviceActionsCollapsed(s.deviceActions);
      if (s.deviceSettings !== undefined) setDeviceSettingsCollapsed(s.deviceSettings);
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId]);

  // --- State Variables ---
  // devices, selectedDeviceId, deviceStates now come from useDevicesStore
  // credentials, credentialsLoaded, patronsMap, pending* now come from useCredentialsStore
  // connectivityStatusUpdateTimes now comes from useDevicesStore
  // isDeviceClaimingModalOpen now comes from useModalManagement hook

  // Organization Settings Hook - handles fetching, caching, and cross-tab sync
  const { orgSettings, setOrgSettings } = useOrganizationSettings(
    organizationId,
    authenticatedFetch,
    propOrgSettings,
  );

  // Credential event tracking
  const {
    lastCredentialDownload,
    setLastCredentialDownload,
    lastCredentialProvisioned,
    setLastCredentialProvisioned,
    lastCredentialRemoved,
    setLastCredentialRemoved,
  } = useCredentialEventTracking();

  // Context synchronization
  useContextSync({
    orgInstallContext,
    isInstallationManagementOpen,
    isOrganizationManagementOpen,
    setInstallationManagementOpen,
    setIsOrganizationManagementOpen,
  });

  // Eligible devices calculations
  const {
    eligibleForLockdown,
    eligibleForRelease,
    eligibleDevicesForLockdown,
    eligibleDevicesForRelease,
    allDeviceStatesLoaded,
  } = useEligibleDevices(devices, deviceStates);

  // --- Refs ---
  // selectedDeviceIdRef now comes from useDeviceSelection hook
  // lastActionRef now comes from useActionHistory hook
  // lastNotificationRef now comes from useNotification hook

  // Debounce timestamp for full sync operations
  const lastFullSyncTimeRef = useRef(0);

  // --- Utility Functions (needed by event handler hooks) ---

  // shouldAllowFullSync - debounce function for full sync operations
  const shouldAllowFullSync = useCallback(() => {
    const now = Date.now();
    if (now - lastFullSyncTimeRef.current < FULL_SYNC_DEBOUNCE_MS) {
      logger.log(
        `[FullSync] ⏭️ SKIPPING - Last sync was ${
          now - lastFullSyncTimeRef.current
        }ms ago (threshold: ${FULL_SYNC_DEBOUNCE_MS}ms)`,
      );
      return false;
    }
    lastFullSyncTimeRef.current = now;
    logger.log(`[FullSync] ✅ ALLOWING sync - threshold passed`);
    return true;
  }, []);

  // --- Event Handler Hooks (after all state and refs are defined) ---

  // Credential Events Hook - handles credential-related WebSocket events
  const credentialEventHandlers = useCredentialEvents({
    selectedDeviceId,
    installationId,
    authenticatedFetch,
    fetchDevices: refreshDevicesFromStore,
    fetchDeviceStateReadOnly,
    invalidateDeviceStateCache,
    setLastCredentialProvisioned,
    setLastCredentialDownload,
    setLastCredentialRemoved,
    setActionHistory,
    showNotification,
  });

  // Device Events Hook - handles device-related WebSocket events
  const deviceEventHandlers = useDeviceEvents({
    installationId,
    selectedDeviceId,
    selectedDeviceIdRef,
    authenticatedFetch,
    invalidateInstallationSyncCache,
    showNotification,
    config,
  });

  // Action Events Hook - handles bulk action tracking WebSocket events
  const actionEventHandlers = useActionEvents({
    currentDeviceId: selectedDeviceId,
    lastActionRef,
    setActionHistory,
    setBulkActionStatuses,
    updateBulkActionTimestamp,
    showNotification,
    MAX_HISTORY_IN_MEMORY,
    checkResetBulkLockdownTracking,
    setLastLockdownEventTime,
    updateDeviceTracking,
    removeStatusFromStore,
    authenticatedFetch,
    installationId,
    fetchDeviceState,
  });

  // Security Events Hook - handles security alert WebSocket events
  const securityEventHandlers = useSecurityEvents({
    selectedDeviceIdRef,
    showNotification,
  });

  // System Events Hook - handles system-level WebSocket events
  const systemEventHandlers = useSystemEvents({
    installationId,
    selectedDeviceIdRef,
    deviceStates,
    devices,
    fetchDevices,
    fetchDeviceStateReadOnly,
    invalidateDeviceStateCache,
    setInstallationSyncCache,
    setSyncing,
    updateMultipleDeviceStates,
    setActionHistory,
    showNotification,
    MAX_HISTORY_IN_MEMORY,
    shouldAllowFullSync,
    getStatusStore,
    orgInstallContext,
  });

  // WebSocket Handlers Hook - provides ref for WebSocket message handler
  const { handleWebSocketMessageRef } = useWebSocketHandlers();

  // Refresh Actions Hook - manages refresh and device action handlers
  const {
    handleRefresh,
    handleDeviceAction,
    handleDownloadSettings,
    handlePasswordConfirm,
    handleUpdateFirmware,
    initiateWifiSettingChange,
    passwordModalPurpose,
  } = useRefreshActions({
    selectedDeviceIdRef,
    authenticatedFetch,
    installationId,
    devices,
    deviceStates,
    connectWebSocketStore,
    refreshDevicesFromStore, // Pass the function from DevicesStore
  });

  // Bulk Actions Hook - manages bulk lockdown operations
  const { handleBulkLockdownAction } = useBulkActions({
    installationId,
    devices,
    deviceStates,
    authenticatedFetch,
    invalidateDeviceStateCache,
  });

  // Credential Management Hook - manages credential deletion
  const {
    handleDeleteCredentialFromDevice,
    handleConfirmDeleteCredential,
    handleCancelDeleteCredential,
    deleteCredentialModal,
  } = useCredentialManagement({
    selectedDeviceId,
    authenticatedFetch,
    pendingDeletionCredentials,
    setPendingDeletionCredentials,
    setDevicesWithPendingDeletions,
    invalidateDeviceStateCache,
  });

  // --- Effects ---

  // Bulk lockdown state synchronization
  // This ensures bulk lockdown eligibility is always up-to-date
  // BUT: Don't run this when the bulk modal is open, as it would overwrite the tracking state
  useBulkLockdownSync(deviceStates, isBulkStatusModalOpen);

  // LocalStorage persistence for UI preferences
  useLocalStoragePersistence(
    theme,
    activeLogo,
    isModalEnabled,
    isAutoReconnectEnabled,
  );

  // Patron data fetching
  usePatronData(credentials, patronsMap, setPatronsMap, authenticatedFetch);

  // Effect to allow escaping the loading modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSyncing(false);
      }
    };

    if (isSyncing) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSyncing, setSyncing]);

  // Action timeout checking
  useActionTimeouts(
    setActionHistory,
    setOfflineDeviceCredentials,
    makePendingKey,
    selectedDeviceIdRef,
  );

  // --- Callbacks ---
  const toggleModalPreference = () => setIsModalEnabled((prev) => !prev);
  const toggleAutoReconnect = () =>
    setAutoReconnectEnabled(!isAutoReconnectEnabled);

  // Display Name Editing
  const {
    isEditing: isEditingDisplayName,
    editingValue: editingDisplayNameValue,
    isSaving: isSavingDisplayName,
    startEditing: handleEditDisplayNameClick,
    handleInputChange: handleDisplayNameInputChange,
    cancelEditing: handleCancelEditDisplayName,
    saveDisplayName: handleSaveDisplayName,
    handleKeyDown: handleDisplayNameKeyDown,
  } = useDisplayNameEditor({
    selectedDeviceId,
    deviceStates,
    updateDeviceDisplayName,
    invalidateDeviceStateCache,
    fetchDeviceState,
    showNotification,
  });

  const credentialsSettingsEditor = useCredentialsSettingsEditor({
    selectedDeviceId,
    updateCredentialsSetting,
    invalidateDeviceStateCache,
    fetchDeviceState,
    showNotification,
    onPasswordProtectedSave: (setting, value, deviceId, onSuccess) =>
      initiateWifiSettingChange(setting, value, deviceId, updateCredentialsSetting, onSuccess),
    getDeviceState: (deviceId) => deviceStates[deviceId],
  });

  const {
    isOpen: isCredentialSettingModalOpen,
    editingSettingKey: credentialSettingKey,
    editingSettingLabel: credentialSettingLabel,
    editingCurrentValue: credentialSettingCurrentValue,
    editingSettingConfig: credentialSettingConfig,
    isSaving: isCredentialSettingSaving,
    saveCredentialsSetting: handleSaveCredentialSetting,
    cancelEditing: handleCancelCredentialSettingEdit,
  } = credentialsSettingsEditor;

  // WebSocket message routing
  const { handleWebSocketMessage } = useWebSocketMessageRouter({
    credentialEventHandlers,
    deviceEventHandlers,
    actionEventHandlers,
    securityEventHandlers,
    systemEventHandlers,
    orgSettings,
    showNotification,
  });

  // Register WebSocket message handler ONCE on mount
  // Get registerMessageHandler without subscribing to avoid re-render loops
  useEffect(() => {
    logger.log("[AppContent] Registering WebSocket message handler with store");

    // Set the handler ref FIRST (before creating wrapper to avoid race condition)
    handleWebSocketMessageRef.current = handleWebSocketMessage;

    // Get registerMessageHandler directly from store state (no subscription)
    const registerMessageHandler =
      useWebSocketStore.getState().registerMessageHandler;

    // Create stable wrapper that will never change
    const stableWrapper = (message) => {
      if (handleWebSocketMessageRef.current) {
        handleWebSocketMessageRef.current(message);
      }
    };

    // Register the stable wrapper with the store
    const unregister = registerMessageHandler(stableWrapper);

    return () => {
      logger.log("[AppContent] Unregistering WebSocket message handler");
      unregister();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - register once on mount

  // Note: handleDeleteCredentialFromDevice, handleConfirmDeleteCredential, handleCancelDeleteCredential
  // are now provided by useCredentialManagement hook

  // Note: handleRefresh, handleDeviceAction, handleDownloadSettings, handlePasswordConfirm
  // are now provided by useRefreshActions hook

  // Note: handleBulkLockdownAction is now provided by useBulkActions hook

  // OPTION 2 + OPTION 4: Functions extracted to useDataLoader hook
  // - loadDataForDevice
  // - triggerInstallationSync
  // - loadInitialDevices

  // Device data loading
  useDeviceDataLoader({
    loadInitialDevices,
    selectedDeviceId,
    isLoading,
    credentialsLoadStartTime,
    setCredentials,
    setActionHistory,
    setCredentialsLoaded,
    loadDataForDevice,
  });

  // WebSocket connection management
  useWebSocketConnection({
    connectWebSocketStore,
    disconnectWebSocketStore,
    handleWebSocketMessage,
    handleWebSocketMessageRef,
  });

  // Installation handlers
  const {
    handleOpenInstallationActions,
    handleOpenInstallationManagement,
    handleRetryInstallationSync,
    handleOpenOrganizationManagement,
    handleSettingsUpdated,
  } = useInstallationHandlers({
    allDeviceStatesLoaded,
    devices,
    deviceStates,
    installationId,
    organizationId,
    setIsInstallationActionsOpen,
    setInstallationManagementOpen,
    setIsOrganizationManagementOpen,
    setOrgSettings,
    triggerInstallationSync,
    invalidateInstallationSyncCache,
  });

  // Organization/Installation Management
  const {
    handleInstallationsChanged,
    handleOrganizationsChanged,
    handleSwitchInstallation: handleSwitchInstallationBase,
    handleSwitchOrganization: handleSwitchOrganizationBase,
  } = useOrganizationManagement({
    organizationId,
    organizations,
    onSwitchOrganization,
    onSwitchInstallation,
    orgInstallContext,
  });

  // Wrap organization management handlers to add modal state updates
  const handleSwitchOrganization = useCallback(
    (organization) => {
      // Close the organization management modal first
      setIsOrganizationManagementOpen(false);
      handleSwitchOrganizationBase(organization);
    },
    [handleSwitchOrganizationBase, setIsOrganizationManagementOpen],
  );

  const handleSwitchInstallation = handleSwitchInstallationBase;

  // Assemble view props using custom hook
  const { patronViewProps, deviceViewProps } = useViewProps({
    theme,
    activeLogo,
    toggleLogo,
    organizationId,
    organizationName,
    installationId,
    installationName,
    orgInstallContext,
    orgSettings,
    navigateToPatrons,
    navigateToDevices,
    openAdvancedSettings,
    handleOpenInstallationActions,
    handleOpenInstallationManagement,
    handleOpenOrganizationManagement,
    onToggleDevStreams,
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    deviceStates,
    deviceViewMode,
    handleViewModeChange,
    allDeviceStatesLoaded,
    handleRefresh,
    isRefreshing,
    setIsDeviceClaimingModalOpen,
    handleDeviceAction,
    handleDownloadSettings,
    handleUpdateFirmware,
    isDownloadingSettings,
    isSyncing,
    loadingAction,
    handleEditDisplayNameClick,
    handleEditCredentialsSettingClick: credentialsSettingsEditor.startEditing,
    connectivityStatusUpdateTimes,
    actionHistory,
    actionHistoryLimit,
    setActionHistoryLimit,
    isDeviceStateCollapsed,
    isDeviceActionsCollapsed,
    isLastActionCollapsed,
    toggleDeviceStateCollapsed,
    toggleDeviceActionsCollapsed,
    toggleLastActionCollapsed,
    isCredentialGridCollapsed,
    toggleCredentialGridCollapsed,
    isDeviceSettingsCollapsed,
    toggleDeviceSettingsCollapsed,
    components,
    handleOnDragEnd,
    credentials,
    credentialsLoaded,
    patronsMap,
    pendingDeletionCredentials,
    offlineDeviceCredentials,
    devicesWithPendingDeletions,
    handleDeleteCredentialFromDevice,
    pendingDeletionPatrons,
    setPendingDeletionPatrons,
    setPendingDeletionCredentials,
    pendingAdditionCredentials,
    setPendingAdditionCredentials,
    pendingFullCredentialDeletions,
    setPendingFullCredentialDeletions,
    devStreamEvents,
    lastCredentialDownload,
    lastCredentialProvisioned,
    lastCredentialRemoved,
  });

  // Assemble modal props using custom hook
  const modalProps = useModalProps({
    isEditingDisplayName,
    editingDisplayNameValue,
    deviceStates,
    selectedDeviceId,
    isSavingDisplayName,
    handleDisplayNameInputChange,
    handleDisplayNameKeyDown,
    handleCancelEditDisplayName,
    handleSaveDisplayName,
    isModalEnabled,
    isAutoReconnectEnabled,
    connectionStatus,
    connectWebSocketStore,
    forcedEntryAlert,
    clearForcedEntryAlert,
    proppedDoorAlert,
    clearProppedDoorAlert,
    eventDeniedAlert,
    clearEventDeniedAlert,
    isAdvancedSettingsOpen,
    organizationId,
    organizationName,
    user,
    handleSettingsUpdated,
    closeAdvancedSettings,
    isDeviceClaimingModalOpen,
    devices,
    handleRefresh,
    setIsDeviceClaimingModalOpen,
    deleteCredentialModal,
    handleConfirmDeleteCredential,
    handleCancelDeleteCredential,
    isInstallationActionsOpen,
    eligibleForLockdown,
    eligibleForRelease,
    eligibleDevicesForLockdown,
    eligibleDevicesForRelease,
    installationName,
    allDeviceStatesLoaded,
    handleBulkLockdownAction,
    handleRetryInstallationSync,
    setIsInstallationActionsOpen,
    handleOpenInstallationManagement,
    isInstallationManagementOpen,
    managedOrganizationId,
    orgInstallContext,
    handleInstallationsChanged,
    handleSwitchInstallation,
    setInstallationManagementOpen,
    isOrganizationManagementOpen,
    handleOrganizationsChanged,
    handleSwitchOrganization,
    closeOrganizationManagementModal,
    theme,
    getAccessToken,
    isBulkStatusModalOpen,
    bulkActionResults,
    bulkActionStatuses,
    bulkActionTimestamps,
    bulkActionType,
    setBulkStatusModalOpen,
    clearBulkActionResults,
    clearBulkActionStatuses,
    clearBulkActionTimestamps,
    showPasswordModal,
    closePasswordModal,
    handlePasswordConfirm,
    passwordModalPurpose,
    authenticatedFetch,
    installationId,
    isCredentialSettingModalOpen,
    credentialSettingKey,
    credentialSettingLabel,
    credentialSettingCurrentValue,
    credentialSettingConfig,
    isCredentialSettingSaving,
    handleSaveCredentialSetting,
    handleCancelCredentialSettingEdit,
  });

  // --- Render Logic ---
  return (
    <div className="App">
      {/* --- Modals Container --- */}
      <ModalsContainer {...modalProps} notification={notification} onCloseNotification={clearNotification} />

      {/* --- Loading Overlay (show during refresh) --- */}
      <LoadingModal isVisible={isRefreshing} onClose={stopRefreshing} />

      {/* --- Main Content --- */}
      {configError ? (
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            color: "var(--error-red)",
          }}
        >
          <div>Configuration Error: {configError}</div>
        </div>
      ) : (
        <ViewContainer
          currentView={currentView}
          patronViewProps={patronViewProps}
          deviceViewProps={deviceViewProps}
        />
      )}

      {/* --- Footer --- */}
      <AppFooter
        connectionStatus={connectionStatus}
        isAutoReconnectEnabled={isAutoReconnectEnabled}
        isModalEnabled={isModalEnabled}
        theme={theme}
        deviceViewMode={deviceViewMode}
        onReconnect={() => connectWebSocketStore(config.websocketUrl)}
        onToggleAutoReconnect={toggleAutoReconnect}
        onToggleModal={toggleModalPreference}
        onToggleTheme={toggleTheme}
        onResetLayout={handleResetLayout}
      />

      {/* WebSocket Debug Panel - Development Only */}
      {process.env.NODE_ENV === "development" && <WebSocketDebugPanel />}
    </div>
  );
}

export default AppContent;
